import {
  Box,
  Dialog,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import type { AlertColor } from "@mui/material/Alert/Alert";
import { AxiosError } from "axios";
import dayjs from "dayjs";
import React from "react";
import { useNavigate } from "react-router-dom";

import type QueryClient from "../../api/query/queryClient";
import { formatMoney } from "../../formatters";
import DataTable, {
  type DataTableColDef,
  type FilterModel,
  type PaginationModel,
  type SortModel,
} from "../DataTable";
import PatientProfileContainer from "../PatientsDashboard/PatientProfileContainer";
import { PatientProfileTabs } from "../PatientsDashboard/views/PatientProfileView";
import CustomNoRowsOverlay from "../TableNoDataOverlay";
import { arActionLabel } from "./arActions";
import { DEFAULT_COLUMN_VISIBILITY, getInsuranceArColumns } from "./columns";
import { buildInsuranceArCsv } from "./export";
import InsuranceArActionDialog, {
  type InsuranceArDialogMode,
} from "./InsuranceArActionDialog";
import InsuranceArNotesPanel from "./InsuranceArNotesPanel";
import InsuranceArRecordCallDialog from "./InsuranceArRecordCallDialog";

export const INSURANCE_AR_PERSIST_KEY = "insuranceArListView";

const AR_PATIENT_MODAL_HIDDEN_CLAIM_COLUMNS = [
  "practiceDisplayName",
  "wieldyPatientName",
  "postedState",
];

export type InsuranceArTabValue = "needswork" | "all";

export interface InsuranceArPagination {
  page: number;
  pageSize: number;
  rowCount: number;
}

interface SummaryCardProps {
  label: string;
  value: string;
  valueColor?: string;
}

function SummaryCard({ label, value, valueColor }: SummaryCardProps) {
  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        minWidth: 0,
        padding: "16px 20px",
        borderRadius: "12px",
        border: "1px solid #EAECF0",
        backgroundColor: "#FFFFFF",
      }}
    >
      <Typography
        sx={{ fontSize: "13px", color: "#475467", marginBottom: "8px" }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: "28px",
          fontWeight: 700,
          color: valueColor ?? "#101828",
          lineHeight: 1.1,
        }}
      >
        {value}
      </Typography>
    </Paper>
  );
}

interface InsuranceArListViewProps {
  overview: InsuranceArOverviewMessage;
  entries: InsuranceArEntryMessage[];
  actionOptions: InsuranceArActionOptionsMessage;
  devMode: boolean;
  loading: boolean;
  queryClient: QueryClient;
  updateSnackBar: (snackbar: { severity: AlertColor; message: string }) => void;
  practices: PracticeMessage[];
  supportedSources: SourceMessage[];
  selectedTab: InsuranceArTabValue;
  onTabChange: (tab: InsuranceArTabValue) => void;
  pagination: InsuranceArPagination;
  onPaginationModelChange: (model: PaginationModel) => void;
  sortModel: SortModel;
  onSortModelChange: (model: SortModel) => void;
  filterModel: FilterModel;
  onFilterModelChange: (model: FilterModel) => void;
  fetchAllEntries: () => Promise<InsuranceArEntryMessage[]>;
  onRefresh: () => void;
}

export default function InsuranceArListView({
  overview,
  entries,
  actionOptions,
  devMode,
  loading,
  queryClient,
  updateSnackBar,
  practices,
  supportedSources,
  selectedTab,
  onTabChange,
  pagination,
  onPaginationModelChange,
  sortModel,
  onSortModelChange,
  filterModel,
  onFilterModelChange,
  fetchAllEntries,
  onRefresh,
}: InsuranceArListViewProps) {
  const navigate = useNavigate();
  const [selectedPatient, setSelectedPatient] = React.useState<{
    patientKey: string;
    payerId: string | null;
    payerName: string | null;
  } | null>(null);
  const [actionDialog, setActionDialog] = React.useState<{
    entry: InsuranceArEntryMessage;
    mode: InsuranceArDialogMode;
    initialActionType?: "DENIAL_ACTION" | "BALANCE_ADJUSTMENT";
    initialAction?: string;
  } | null>(null);
  const [callDialogEntry, setCallDialogEntry] =
    React.useState<InsuranceArEntryMessage | null>(null);
  const [openNotesRowId, setOpenNotesRowId] = React.useState<string | null>(
    null,
  );
  const [confirmingEntryIds, setConfirmingEntryIds] = React.useState<
    Set<string>
  >(new Set());
  const [isExporting, setIsExporting] = React.useState(false);

  const isMultiPractice = overview.reports.length > 1;
  const columnVisibilityOverrides = React.useMemo(
    () => ({ practiceName: isMultiPractice }),
    [isMultiPractice],
  );

  const practiceNames = React.useMemo(
    () =>
      practices.reduce<Record<string, string>>((acc, practice) => {
        acc[practice.wieldyId] = practice.displayName;
        return acc;
      }, {}),
    [practices],
  );

  const onPatientClick = React.useCallback((entry: InsuranceArEntryMessage) => {
    if (!entry.patientKey) return;
    setSelectedPatient({
      patientKey: entry.patientKey,
      payerId: entry.payerId,
      payerName: entry.payerName ?? entry.insuranceNameRaw,
    });
  }, []);

  const onWorkInDenise = React.useCallback(
    (entry: InsuranceArEntryMessage) => {
      navigate(
        `/claims?status=review&view=denise&claim_key=${entry.matchingDenialKey}`,
      );
    },
    [navigate],
  );
  const onRecordWork = React.useCallback(
    (entry: InsuranceArEntryMessage) =>
      setActionDialog({ entry, mode: "work" }),
    [],
  );
  const onRecordAdjustment = React.useCallback(
    (entry: InsuranceArEntryMessage) =>
      setActionDialog({ entry, mode: "adjustment" }),
    [],
  );

  const postArUpdate = React.useCallback(
    (
      entry: InsuranceArEntryMessage,
      actionType: "DENIAL_ACTION" | "BALANCE_ADJUSTMENT",
      action: string,
      confirmAction: boolean,
    ) =>
      queryClient.updateInsuranceArEntry({
        entryId: entry.id,
        practiceId: entry.practiceId,
        actionType,
        action,
        confirmAction,
      }),
    [queryClient],
  );

  const notifyError = React.useCallback(
    (error: unknown, fallback: string) => {
      updateSnackBar({
        severity: "error",
        message:
          error instanceof AxiosError && error.response?.data?.detail
            ? String(error.response.data.detail)
            : fallback,
      });
    },
    [updateSnackBar],
  );

  const onEditNextStep = React.useCallback((entry: InsuranceArEntryMessage) => {
    const completed = entry.status === "WORK_COMPLETED";
    setActionDialog({
      entry,
      mode: entry.isDeniseManaged ? "adjustment" : "work",
      initialActionType: completed
        ? "BALANCE_ADJUSTMENT"
        : (entry.plannedActionType ?? undefined),
      initialAction: completed
        ? (entry.adjustmentAction ?? undefined)
        : (entry.plannedAction ?? undefined),
    });
  }, []);

  const onConfirmPlan = React.useCallback(
    async (entry: InsuranceArEntryMessage) => {
      if (!entry.plannedAction || !entry.plannedActionType) return;
      setConfirmingEntryIds((prev) => new Set(prev).add(entry.id));
      try {
        await postArUpdate(
          entry,
          entry.plannedActionType,
          entry.plannedAction,
          true,
        );
        updateSnackBar({
          severity: "success",
          message: `Confirmed ${arActionLabel(entry.plannedAction)}.`,
        });
        onRefresh();
      } catch (error: unknown) {
        notifyError(error, "Failed to confirm the action.");
      } finally {
        setConfirmingEntryIds((prev) => {
          const next = new Set(prev);
          next.delete(entry.id);
          return next;
        });
      }
    },
    [postArUpdate, updateSnackBar, onRefresh, notifyError],
  );

  const onRecordCall = React.useCallback(
    (entry: InsuranceArEntryMessage) => setCallDialogEntry(entry),
    [],
  );

  const onToggleNotes = React.useCallback(
    (entry: InsuranceArEntryMessage) =>
      setOpenNotesRowId((current) => (current === entry.id ? null : entry.id)),
    [],
  );

  const renderNotesPanel = React.useCallback(
    (row: InsuranceArEntryMessage) => (
      <InsuranceArNotesPanel
        entry={row}
        practiceId={row.practiceId}
        queryClient={queryClient}
        updateSnackBar={updateSnackBar}
        notifyError={notifyError}
        onRefresh={onRefresh}
      />
    ),
    [queryClient, updateSnackBar, notifyError, onRefresh],
  );

  const columns = React.useMemo(
    () =>
      getInsuranceArColumns(
        devMode,
        onPatientClick,
        practiceNames,
        {
          onWorkInDenise,
          onRecordWork,
          onRecordAdjustment,
          onConfirmPlan,
          onRecordCall,
          onEditNextStep,
        },
        onToggleNotes,
        openNotesRowId,
        confirmingEntryIds,
      ),
    [
      devMode,
      onPatientClick,
      practiceNames,
      onWorkInDenise,
      onRecordWork,
      onRecordAdjustment,
      onConfirmPlan,
      onRecordCall,
      onEditNextStep,
      onToggleNotes,
      openNotesRowId,
      confirmingEntryIds,
    ],
  );

  const onExport = async (
    exportColumns: DataTableColDef<InsuranceArEntryMessage>[],
  ) => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const rows = await fetchAllEntries();
      if (rows.length === 0) {
        updateSnackBar({
          severity: "error",
          message: "No rows to export with the current filters.",
        });
        return;
      }
      const csv = buildInsuranceArCsv(exportColumns, rows);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `insurance-ar-export-${dayjs().format("YYYY-MM-DD")}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (error: unknown) {
      notifyError(error, "Failed to export the insurance A/R report.");
    } finally {
      setIsExporting(false);
    }
  };

  const onActionConfirm = async (
    actionType: "DENIAL_ACTION" | "BALANCE_ADJUSTMENT",
    action: string,
  ) => {
    if (!actionDialog) return;
    try {
      await postArUpdate(actionDialog.entry, actionType, action, false);
      updateSnackBar({
        severity: "success",
        message: `Next step set — ${arActionLabel(
          action,
        )} awaiting confirmation.`,
      });
      setActionDialog(null);
      onRefresh();
    } catch (error: unknown) {
      notifyError(error, "Failed to record the action.");
    }
  };

  const onCallConfirm = async (nextAction: string) => {
    if (!callDialogEntry) return;
    try {
      await postArUpdate(callDialogEntry, "DENIAL_ACTION", "CALL_PAYER", true);
      await postArUpdate(callDialogEntry, "DENIAL_ACTION", nextAction, false);
      updateSnackBar({
        severity: "success",
        message: `Call recorded — next step set to ${arActionLabel(
          nextAction,
        )}.`,
      });
      setCallDialogEntry(null);
      onRefresh();
    } catch (error: unknown) {
      notifyError(error, "Failed to record the call.");
    }
  };

  const { totals } = overview;
  const soleReport = overview.reports.length === 1 ? overview.reports[0] : null;
  const practicesWithoutReport = overview.reports
    .filter((report) => !report.reportId)
    .map((report) => practiceNames[report.practiceId] ?? report.practiceId);

  return (
    <>
      <Box sx={{ marginBottom: "20px" }}>
        <Typography
          sx={{
            fontSize: "20px",
            fontWeight: 700,
            color: "#101828",
            lineHeight: 1.2,
          }}
        >
          {soleReport
            ? (practiceNames[soleReport.practiceId] ?? "Insurance A/R")
            : `${overview.reports.length} practices`}
        </Typography>
        {soleReport?.pulledAt ? (
          <Typography
            sx={{ fontSize: "13px", color: "#667085", marginTop: "2px" }}
          >
            {soleReport.sourceName} · pulled{" "}
            {dayjs(soleReport.pulledAt).format("MMM D, YYYY")}
          </Typography>
        ) : null}
        {practicesWithoutReport.length > 0 && !soleReport ? (
          <Typography
            sx={{ fontSize: "13px", color: "#98A2B3", marginTop: "2px" }}
          >
            No A/R report yet for: {practicesWithoutReport.join(", ")}
          </Typography>
        ) : null}
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: "16px",
          flexWrap: "wrap",
          marginBottom: "24px",
        }}
      >
        <SummaryCard
          label="Total Insurance A/R"
          value={formatMoney(totals.totalAr)}
        />
        <SummaryCard
          label="30–60 Days"
          value={formatMoney(totals.aging31To60)}
        />
        <SummaryCard
          label="60–90 Days"
          value={formatMoney(totals.aging61To90)}
          valueColor="#B54708"
        />
        <SummaryCard label="90+ Days" value={formatMoney(totals.aging90Plus)} />
      </Box>

      <ToggleButtonGroup
        size="medium"
        color="primary"
        value={selectedTab}
        exclusive
        onChange={(_, newTab: InsuranceArTabValue | null) => {
          if (newTab && newTab !== selectedTab) {
            onTabChange(newTab);
          }
        }}
        sx={{ marginBottom: "16px" }}
        aria-label="Insurance A/R views"
      >
        <ToggleButton value="needswork">
          Needs Work ({overview.needsWorkCount})
        </ToggleButton>
        <ToggleButton value="all">All ({overview.totalCount})</ToggleButton>
      </ToggleButtonGroup>

      <DataTable
        loading={loading}
        persistKey={INSURANCE_AR_PERSIST_KEY}
        rows={entries}
        columns={columns}
        getRowId={(row) => row.id}
        rowCount={pagination.rowCount}
        paginationModel={{
          page: pagination.page - 1,
          pageSize: pagination.pageSize,
        }}
        pageSizeOptions={[10, 25, 50, 100]}
        onPaginationModelChange={onPaginationModelChange}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        filterModel={filterModel}
        onFilterModelChange={onFilterModelChange}
        initialColumnVisibility={DEFAULT_COLUMN_VISIBILITY}
        columnVisibilityOverrides={columnVisibilityOverrides}
        expandedRowIds={openNotesRowId ? [openNotesRowId] : []}
        renderDetailPanel={renderNotesPanel}
        onExportCsv={onExport}
        exporting={isExporting}
        noRowsOverlay={<CustomNoRowsOverlay />}
      />

      <InsuranceArActionDialog
        entry={actionDialog?.entry ?? null}
        mode={actionDialog?.mode ?? "work"}
        actionOptions={actionOptions}
        initialActionType={actionDialog?.initialActionType}
        initialAction={actionDialog?.initialAction}
        onClose={() => setActionDialog(null)}
        onConfirm={onActionConfirm}
      />

      <InsuranceArRecordCallDialog
        entry={callDialogEntry}
        callNextStepActions={actionOptions.callNextStepActions}
        onClose={() => setCallDialogEntry(null)}
        onConfirm={onCallConfirm}
      />

      <Dialog
        open={selectedPatient !== null}
        onClose={() => setSelectedPatient(null)}
        fullWidth
        maxWidth="lg"
        scroll="body"
      >
        {selectedPatient !== null && (
          <Box sx={{ padding: "0 24px 24px" }}>
            <PatientProfileContainer
              patientId={selectedPatient.patientKey}
              onClose={() => setSelectedPatient(null)}
              onDenialUpdated={onRefresh}
              updateSnackBar={updateSnackBar}
              practices={practices}
              supportedSources={supportedSources}
              initialTab={PatientProfileTabs.InsurancePayments}
              payerFilter={
                selectedPatient.payerId
                  ? {
                      payerId: selectedPatient.payerId,
                      payerName: selectedPatient.payerName,
                    }
                  : null
              }
              excludeClaimColumns={AR_PATIENT_MODAL_HIDDEN_CLAIM_COLUMNS}
            />
          </Box>
        )}
      </Dialog>
    </>
  );
}
